import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import {
  apiError, ATTENDANCE_LABELS, formatSessionDate, formatSessionWindow, trainingService, TrainingEnrollment, TrainingSemester as Semester,
  TrainingSession,
} from '../api/trainingService';
import {
  buttonClass, ErrorText, inputClass, secondaryButtonClass, SessionForm, StatusPill, TrainerSlots, useTrainingRoles, VenueForm,
} from '../components/training/TrainingShared';

type Tab = 'schedule' | 'trainers' | 'enrollment' | 'attendance' | 'settings';

// ---------- Schedule ----------

const SessionCard: React.FC<{ session: TrainingSession; canAssign: boolean }> = ({ session: s, canAssign }) => {
  const past = new Date(s.startsAt) < new Date();
  return (
    <div className={`bg-white rounded-xl shadow-sm p-4 sm:p-5 ${s.status === 'cancelled' ? 'opacity-60' : ''}`}>
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="lg:w-44 shrink-0">
          <p className="font-semibold text-gray-900">{formatSessionDate(s.startsAt, s.timezone)}</p>
          <p className="text-sm text-gray-600">{formatSessionWindow(s.startsAt, s.endsAt, s.timezone)}</p>
          {s.status === 'cancelled' && <StatusPill status="cancelled" />}
          {past && s.status !== 'cancelled' && <span className="text-xs text-gray-500">Past</span>}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <Link to={`/training/sessions/${s.id}`} className="text-lg font-semibold text-primary-700 hover:underline">{s.displayTitle}</Link>
          <p className="text-sm text-gray-700">
            {s.venue ? <>{s.venue.name}{s.venue.address ? ` · ${s.venue.address}` : <span className="text-amber-700"> · address needed</span>}</> : <span className="text-amber-700">Location to be announced</span>}
          </p>
          {s.outcomes.length > 0 && (
            <ul className="text-sm text-gray-600 list-disc pl-5">
              {s.outcomes.map((o, i) => <li key={o.id ?? i} className={o.isDraft ? 'italic text-gray-400' : ''}>{o.text}{o.isDraft ? ' (draft)' : ''}</li>)}
            </ul>
          )}
          {s.notes && <p className="text-sm italic text-gray-500">{s.notes}</p>}
        </div>
        <div className="lg:w-72 shrink-0">
          <TrainerSlots session={s} canAssign={canAssign} compact />
        </div>
      </div>
    </div>
  );
};

const ScheduleTab: React.FC<{ semester: Semester & { sessions: TrainingSession[] } }> = ({ semester }) => {
  const { isStaff, isTrainer, userId } = useTrainingRoles();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState<'all' | 'mine' | 'open'>('all');
  const create = useMutation({
    mutationFn: (input: Parameters<typeof trainingService.createSession>[1]) => trainingService.createSession(semester.id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['training'] }); setAdding(false); },
  });
  const sessions = semester.sessions.filter((s) => {
    if (filter === 'mine') return s.trainers.some((t) => t.trainer?.id === userId);
    if (filter === 'open') return s.status !== 'cancelled' && s.trainers.some((t) => !t.trainer);
    return true;
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {(['all', ...(isTrainer ? ['mine'] : []), 'open'] as const).map((f) => (
          <button key={f} className={`px-3 py-1.5 rounded-full text-sm ${filter === f ? 'bg-primary-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`} onClick={() => setFilter(f as typeof filter)}>
            {f === 'all' ? 'All sessions' : f === 'mine' ? 'My sessions' : 'Open trainer slots'}
          </button>
        ))}
        {isStaff && !adding && <button className={`${buttonClass} ml-auto`} onClick={() => setAdding(true)}>Add session</button>}
      </div>
      {adding && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="font-semibold mb-3">New session</h3>
          <SessionForm defaultStartTime={semester.defaultStartTime} defaultDurationMinutes={semester.defaultDurationMinutes} submitLabel="Add session"
            pending={create.isPending} error={create.error} onSubmit={(input) => create.mutate(input)} onCancel={() => setAdding(false)} />
        </div>
      )}
      {sessions.length === 0 && <p className="text-gray-600">No sessions match.</p>}
      {sessions.map((s) => <SessionCard key={s.id} session={s} canAssign={isStaff} />)}
    </div>
  );
};

// ---------- Trainers load ----------

const TrainersTab: React.FC<{ sessions: TrainingSession[] }> = ({ sessions }) => {
  const live = sessions.filter((s) => s.status !== 'cancelled');
  const load = useMemo(() => {
    const byTrainer = new Map<number, { name: string; sessions: TrainingSession[] }>();
    for (const s of live) {
      for (const t of s.trainers) {
        if (!t.trainer) continue;
        const entry = byTrainer.get(t.trainer.id) || { name: t.trainer.name, sessions: [] };
        entry.sessions.push(s);
        byTrainer.set(t.trainer.id, entry);
      }
    }
    return Array.from(byTrainer.values()).sort((a, b) => b.sessions.length - a.sessions.length || a.name.localeCompare(b.name));
  }, [live]);
  const open = live.flatMap((s) => s.trainers.filter((t) => !t.trainer).map((t) => ({ s, slot: t.slot })));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="font-semibold mb-3">Trainer load</h3>
        {load.length === 0 && <p className="text-sm text-gray-600">No trainers assigned yet.</p>}
        <ul className="divide-y divide-gray-100">
          {load.map((t) => (
            <li key={t.name} className="py-2">
              <div className="flex justify-between"><span className="font-medium">{t.name}</span><span className="text-sm text-gray-600">{t.sessions.length} session{t.sessions.length === 1 ? '' : 's'}</span></div>
              <p className="text-xs text-gray-500">{t.sessions.map((s) => formatSessionDate(s.startsAt, s.timezone)).join(' · ')}</p>
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="font-semibold mb-3">Open slots ({open.length})</h3>
        {open.length === 0 && <p className="text-sm text-green-700">Every session is staffed.</p>}
        <ul className="divide-y divide-gray-100">
          {open.map(({ s, slot }) => (
            <li key={`${s.id}-${slot}`} className="py-2 text-sm">
              <Link className="text-primary-700 hover:underline" to={`/training/sessions/${s.id}`}>{s.displayTitle}</Link>
              <span className="text-gray-500"> · {formatSessionDate(s.startsAt, s.timezone)} · {slot === 'lead' ? 'lead' : 'co'} slot</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ---------- Enrollment ----------

const AddPlanters: React.FC<{ semesterId: number; onDone: () => void }> = ({ semesterId, onDone }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const eligible = useQuery({ queryKey: ['training', 'eligible', semesterId, search], queryFn: () => trainingService.eligiblePlanters(semesterId, search || undefined) });
  const enroll = useMutation({
    mutationFn: () => trainingService.enroll(semesterId, selected),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['training'] }); onDone(); },
  });
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 space-y-3">
      <h3 className="font-semibold">Add planters in the training step</h3>
      <input aria-label="Search planters" className={inputClass} placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
      {eligible.isLoading && <p className="text-sm text-gray-600">Loading…</p>}
      {eligible.data?.length === 0 && <p className="text-sm text-gray-600">No training-step planters left to add.</p>}
      <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100">
        {(eligible.data || []).map((p) => (
          <li key={p.id}>
            <label className="flex items-center gap-3 py-2 text-sm">
              <input type="checkbox" checked={selected.includes(p.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, p.id] : selected.filter((id) => id !== p.id))} />
              <span className="font-medium">{p.name}{p.spouseName ? ` & ${p.spouseName}` : ''}</span>
              <span className="text-gray-500 truncate">{p.email || 'no email'}{p.city ? ` · ${p.city}, ${p.state || ''}` : ''}</span>
              {!p.hasLogin && <span className="text-xs text-amber-700 whitespace-nowrap">no portal login</span>}
            </label>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <button className={buttonClass} disabled={!selected.length || enroll.isPending} onClick={() => enroll.mutate()}>Enroll {selected.length || ''}</button>
        <button className={secondaryButtonClass} onClick={onDone}>Cancel</button>
      </div>
      <ErrorText error={enroll.error} fallback="Unable to enroll" />
    </div>
  );
};

const CompleteButton: React.FC<{ enrollment: TrainingEnrollment }> = ({ enrollment: e }) => {
  const qc = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const complete = useMutation({
    mutationFn: (override: boolean) => trainingService.completeEnrollment(e.id, { override, advancePipeline: true }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['training'] });
      setResult(r.pipeline.advanced ? 'Completed and moved to funding.' : `Completed. ${r.pipeline.reason || ''}`);
    },
  });
  const run = () => {
    const s = e.summary;
    if (!s.meetsRequirement) {
      if (!window.confirm(`${e.planter?.name} attended ${s.attended} of the ${s.required} required sessions. Complete anyway?`)) return;
      complete.mutate(true);
      return;
    }
    if (window.confirm(`Mark ${e.planter?.name} complete? This also moves them from training to funding in the pipeline.`)) complete.mutate(false);
  };
  return (
    <>
      <button className="text-sm text-green-700 hover:underline disabled:opacity-50" disabled={complete.isPending} onClick={run}>Mark complete</button>
      {result && <span className="text-xs text-gray-600 block">{result}</span>}
      {complete.error && <span className="text-xs text-red-700 block">{apiError(complete.error)}</span>}
    </>
  );
};

const EnrollmentTab: React.FC<{ semester: Semester }> = ({ semester }) => {
  const { isStaff } = useTrainingRoles();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const enrollments = useQuery({ queryKey: ['training', 'enrollments', semester.id], queryFn: () => trainingService.enrollments(semester.id) });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'enrolled' | 'withdrawn' }) => trainingService.setEnrollmentStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training'] }),
  });
  return (
    <div className="space-y-4">
      {isStaff && !adding && <button className={buttonClass} onClick={() => setAdding(true)}>Add planters</button>}
      {adding && <AddPlanters semesterId={semester.id} onDone={() => setAdding(false)} />}
      {semester.status === 'draft' && <p className="text-sm text-gray-600">Welcome emails go out when the semester is published.</p>}
      <ErrorText error={enrollments.error || setStatus.error} />
      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr><th className="px-4 py-2">Planter</th><th className="px-4 py-2">Email</th><th className="px-4 py-2">Attended</th><th className="px-4 py-2">Status</th>{isStaff && <th className="px-4 py-2" />}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(enrollments.data || []).map((e) => (
              <tr key={e.id} className={e.status === 'withdrawn' ? 'text-gray-400' : ''}>
                <td className="px-4 py-2 font-medium">{e.planter?.name}{e.planter?.spouseName ? ` & ${e.planter.spouseName}` : ''}</td>
                <td className="px-4 py-2">{e.planter?.email || <span className="text-amber-700">missing — no emails</span>}</td>
                <td className="px-4 py-2">
                  <span className={e.summary.meetsRequirement ? 'text-green-700 font-medium' : ''}>{e.summary.attended}</span> / {e.summary.required} required
                </td>
                <td className="px-4 py-2"><StatusPill status={e.status} /></td>
                {isStaff && (
                  <td className="px-4 py-2 space-x-3 whitespace-nowrap">
                    {e.status === 'enrolled' && <CompleteButton enrollment={e} />}
                    {e.status === 'enrolled' && <button className="text-sm text-gray-500 hover:text-red-600" onClick={() => window.confirm('Withdraw this planter? They will stop getting reminders.') && setStatus.mutate({ id: e.id, status: 'withdrawn' })}>Withdraw</button>}
                    {e.status === 'withdrawn' && <button className="text-sm text-primary-700 hover:underline" onClick={() => setStatus.mutate({ id: e.id, status: 'enrolled' })}>Re-enroll</button>}
                  </td>
                )}
              </tr>
            ))}
            {enrollments.data?.length === 0 && <tr><td className="px-4 py-4 text-gray-600" colSpan={5}>No planters enrolled yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Attendance grid ----------

const cellStyle: Record<string, string> = {
  present: 'bg-green-100 text-green-800', late: 'bg-lime-100 text-lime-800', excused: 'bg-amber-100 text-amber-800', absent: 'bg-red-100 text-red-700',
};

const AttendanceTab: React.FC<{ semester: Semester & { sessions: TrainingSession[] } }> = ({ semester }) => {
  const enrollments = useQuery({ queryKey: ['training', 'enrollments', semester.id], queryFn: () => trainingService.enrollments(semester.id) });
  const sessions = semester.sessions.filter((s) => s.status !== 'cancelled');
  const rows = (enrollments.data || []).filter((e) => e.status !== 'withdrawn');
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Completion requires {semester.completionRequiredSessions ?? 'all'} attended sessions (present or late). Take attendance from each session’s page.
      </p>
      <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left sticky left-0 bg-gray-50">Planter</th>
              {sessions.map((s, i) => (
                <th key={s.id} className="px-2 py-2 text-center whitespace-nowrap">
                  <Link to={`/training/sessions/${s.id}`} title={s.displayTitle} className="hover:underline">{i + 1}<br />{new Date(s.startsAt).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', timeZone: s.timezone })}</Link>
                </th>
              ))}
              <th className="px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-2 font-medium sticky left-0 bg-white whitespace-nowrap">{e.planter?.name}</td>
                {sessions.map((s) => {
                  const a = e.attendance.find((x) => x.sessionId === s.id);
                  return (
                    <td key={s.id} className="px-1 py-1 text-center">
                      {a ? <span title={ATTENDANCE_LABELS[a.status]} className={`inline-block w-7 py-0.5 rounded ${cellStyle[a.status]}`}>{a.status[0].toUpperCase()}</span> : <span className="text-gray-300">·</span>}
                    </td>
                  );
                })}
                <td className={`px-3 py-2 text-center font-semibold ${e.summary.meetsRequirement ? 'text-green-700' : 'text-gray-800'}`}>{e.summary.attended}/{e.summary.required}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="px-3 py-4 text-gray-600" colSpan={sessions.length + 2}>No planters enrolled.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">P present · L late · E excused · A absent</p>
    </div>
  );
};

// ---------- Settings ----------

const SettingsTab: React.FC<{ semester: Semester & { sessions: TrainingSession[] } }> = ({ semester }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: semester.name,
    trainerManualUrl: semester.trainerManualUrl || '',
    planterManualUrl: semester.planterManualUrl || '',
    defaultStartTime: semester.defaultStartTime,
    defaultDurationMinutes: String(semester.defaultDurationMinutes),
    completionRequiredSessions: semester.completionRequiredSessions == null ? '' : String(semester.completionRequiredSessions),
  });
  const venues = useQuery({ queryKey: ['training', 'venues'], queryFn: trainingService.venues });
  const [editingVenue, setEditingVenue] = useState<number | 'new' | null>(null);
  const save = useMutation({
    mutationFn: (patch: Partial<Semester>) => trainingService.updateSemester(semester.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training'] }),
  });
  const remove = useMutation({ mutationFn: () => trainingService.deleteSemester(semester.id), onSuccess: () => navigate('/training') });
  const saveForm = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate({
      name: form.name,
      trainerManualUrl: form.trainerManualUrl || null,
      planterManualUrl: form.planterManualUrl || null,
      defaultStartTime: form.defaultStartTime,
      defaultDurationMinutes: Number(form.defaultDurationMinutes),
      completionRequiredSessions: form.completionRequiredSessions === '' ? null : Number(form.completionRequiredSessions),
    });
  };
  const live = semester.sessions.filter((s) => s.status !== 'cancelled').length;
  const missingAddress = semester.sessions.filter((s) => s.status !== 'cancelled' && (!s.venue || !s.venue.address)).length;
  const open = semester.sessions.filter((s) => s.status !== 'cancelled').reduce((n, s) => n + s.trainers.filter((t) => !t.trainer).length, 0);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={saveForm} className="bg-white rounded-xl shadow-sm p-5 space-y-3">
        <h3 className="font-semibold">Semester settings</h3>
        <label className="block text-sm"><span className="text-gray-700">Name</span><input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="block text-sm"><span className="text-gray-700">Trainer manual link</span><input className={inputClass} type="url" value={form.trainerManualUrl} onChange={(e) => setForm({ ...form, trainerManualUrl: e.target.value })} /></label>
        <label className="block text-sm"><span className="text-gray-700">Planter manual link</span><input className={inputClass} type="url" value={form.planterManualUrl} onChange={(e) => setForm({ ...form, planterManualUrl: e.target.value })} /></label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block text-sm"><span className="text-gray-700">Default start (Central)</span><input className={inputClass} type="time" value={form.defaultStartTime} onChange={(e) => setForm({ ...form, defaultStartTime: e.target.value })} /></label>
          <label className="block text-sm"><span className="text-gray-700">Default length (min)</span><input className={inputClass} type="number" min={15} value={form.defaultDurationMinutes} onChange={(e) => setForm({ ...form, defaultDurationMinutes: e.target.value })} /></label>
          <label className="block text-sm"><span className="text-gray-700">Sessions required</span><input className={inputClass} type="number" min={0} max={live} placeholder={`all (${live})`} value={form.completionRequiredSessions} onChange={(e) => setForm({ ...form, completionRequiredSessions: e.target.value })} /></label>
        </div>
        <p className="text-xs text-gray-500">Completion counts sessions marked present or late, out of {live} scheduled. Leave blank to require every session.</p>
        <button className={buttonClass} type="submit" disabled={save.isPending}>Save settings</button>
        {save.isSuccess && <span className="ml-3 text-sm text-green-700">Saved</span>}
        <ErrorText error={save.error} fallback="Unable to save" />
      </form>

      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-3">
          <h3 className="font-semibold">Status: <StatusPill status={semester.status} /></h3>
          {semester.status === 'draft' && (
            <>
              <p className="text-sm text-gray-600">
                Publishing lets trainers see and claim sessions, emails assigned trainers their confirmations, and emails enrolled planters their schedule. Scheduled reminders start after that.
              </p>
              {(open > 0 || missingAddress > 0) && (
                <ul className="text-sm text-amber-700 list-disc pl-5">
                  {open > 0 && <li>{open} trainer slot{open === 1 ? ' is' : 's are'} still open.</li>}
                  {missingAddress > 0 && <li>{missingAddress} session{missingAddress === 1 ? ' has' : 's have'} no venue street address.</li>}
                </ul>
              )}
              <div className="flex gap-2">
                <button className={buttonClass} disabled={save.isPending} onClick={() => window.confirm('Publish this semester and send emails?') && save.mutate({ status: 'published' })}>Publish</button>
                <button className="text-sm text-red-600 hover:underline" onClick={() => window.confirm('Delete this draft semester and all its sessions?') && remove.mutate()}>Delete draft</button>
              </div>
            </>
          )}
          {semester.status === 'published' && (
            <button className={secondaryButtonClass} onClick={() => window.confirm('Archive this semester? Reminders stop and trainers no longer see it.') && save.mutate({ status: 'archived' })}>Archive</button>
          )}
          {semester.status === 'archived' && (
            <button className={secondaryButtonClass} onClick={() => save.mutate({ status: 'published' })}>Restore to published</button>
          )}
          <ErrorText error={remove.error} />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5 space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Venues</h3>
            {editingVenue !== 'new' && <button className={secondaryButtonClass} onClick={() => setEditingVenue('new')}>Add venue</button>}
          </div>
          {editingVenue === 'new' && <VenueForm onSaved={() => setEditingVenue(null)} onCancel={() => setEditingVenue(null)} />}
          <ul className="divide-y divide-gray-100">
            {(venues.data || []).map((v) => (
              <li key={v.id} className="py-2">
                {editingVenue === v.id ? (
                  <VenueForm venue={v} onSaved={() => setEditingVenue(null)} onCancel={() => setEditingVenue(null)} />
                ) : (
                  <div className="flex justify-between gap-2">
                    <div>
                      <p className="font-medium">{v.name} {!v.isActive && <span className="text-xs text-gray-500">(retired)</span>}</p>
                      <p className="text-sm text-gray-600">{v.address || <span className="text-amber-700">No street address</span>}</p>
                    </div>
                    <button className="text-sm text-primary-700 hover:underline" onClick={() => setEditingVenue(v.id)}>Edit</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ---------- Page ----------

const TrainingSemesterPage: React.FC = () => {
  const { id } = useParams();
  const semesterId = Number(id);
  const { isStaff } = useTrainingRoles();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'schedule';
  const semester = useQuery({ queryKey: ['training', 'semester', semesterId], queryFn: () => trainingService.semester(semesterId) });

  const tabs: { key: Tab; label: string }[] = [
    { key: 'schedule', label: 'Schedule' },
    { key: 'trainers', label: 'Trainers' },
    { key: 'enrollment', label: 'Planters' },
    { key: 'attendance', label: 'Attendance' },
    ...(isStaff ? [{ key: 'settings' as Tab, label: 'Settings' }] : []),
  ];

  return (
    <Layout>
      <div className="space-y-5">
        <Link to="/training" className="text-sm text-primary-700 hover:underline">← All semesters</Link>
        {semester.isLoading && <p className="text-gray-600">Loading…</p>}
        <ErrorText error={semester.error} fallback="Unable to load semester" />
        {semester.data && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{semester.data.name}</h1>
              <StatusPill status={semester.data.status} />
              <div className="ml-auto flex gap-3 text-sm">
                {semester.data.trainerManualUrl && <a className="text-primary-700 hover:underline" href={semester.data.trainerManualUrl} target="_blank" rel="noreferrer">Trainer manual</a>}
                {semester.data.planterManualUrl && <a className="text-primary-700 hover:underline" href={semester.data.planterManualUrl} target="_blank" rel="noreferrer">Planter manual</a>}
              </div>
            </div>
            <div className="border-b border-gray-200 flex gap-4 overflow-x-auto">
              {tabs.map((t) => (
                <button key={t.key} onClick={() => setParams({ tab: t.key })}
                  className={`pb-2 text-sm font-medium border-b-2 whitespace-nowrap ${tab === t.key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            {tab === 'schedule' && <ScheduleTab semester={semester.data} />}
            {tab === 'trainers' && <TrainersTab sessions={semester.data.sessions} />}
            {tab === 'enrollment' && <EnrollmentTab semester={semester.data} />}
            {tab === 'attendance' && <AttendanceTab semester={semester.data} />}
            {tab === 'settings' && isStaff && <SettingsTab key={semester.data.id + semester.data.status} semester={semester.data} />}
          </>
        )}
      </div>
    </Layout>
  );
};

export default TrainingSemesterPage;
